import React, { useState, useEffect, useCallback, useRef } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
  Phone,
  Plus,
  Search,
  Globe,
  User,
  ArrowRightLeft,
  X,
  AlertTriangle,
  Loader2,
  Tag,
  MapPin,
  ChevronRight,
  ChevronLeft,
  Trash2,
  Edit3,
  Shield,
  Upload,
  FileText,
  CheckCircle2,
  Lock,
  Info,
} from "lucide-react";
import { twilioApi, usersApi, PaginatedResponse } from "../../services/api";
import { useUserDataContext } from "../../context/UserDataContext";
import SearchableSelect, { SelectOption } from "../ui/SearchableSelect";
import PremiumModal from "../ui/PremiumModal";

// Province → major city for auto-populating search
const PROVINCE_MAJOR_CITY: Record<string, string> = {
  ON: "Toronto",
  QC: "Montreal",
  BC: "Vancouver",
  AB: "Calgary",
  MB: "Winnipeg",
  SK: "Saskatoon",
  NS: "Halifax",
  NB: "Moncton",
  NL: "St. John's",
  PE: "Charlottetown",
  NT: "Yellowknife",
  NU: "Iqaluit",
  YT: "Whitehorse",
};

// ── Types ────────────────────────────────────────────────────────────

interface TwilioNumber {
  _id: string;
  phoneE164: string;
  friendlyName?: string;
  provisioning: {
    method: string;
    status: string;
    porting?: {
      carrier?: string;
      status?: string;
    };
  };
  jurisdiction: {
    country: string;
    region?: string;
    city?: string;
  };
  usage: {
    purpose: string;
    active: boolean;
    defaultAssignedUserId?: string;
  };
  compliance: {
    requireOptIn: boolean;
    stopKeywordsEnabled: boolean;
    consentPolicy: string;
  };
  operational?: {
    lastInboundAt?: string;
    lastOutboundAt?: string;
  };
  createdAt: string;
}

interface AvailableNumber {
  phoneNumber: string;
  friendlyName: string;
  city?: string;
  region?: string;
  country: string;
  capabilities: { sms: boolean; voice: boolean; mms: boolean };
}

interface UserItem {
  _id: string;
  name?: string;
  email?: string;
}

// ── Helpers ──────────────────────────────────────────────────────────

const statusBadge = (status: string) => {
  switch (status) {
    case "active":
      return "bg-emerald-100 text-emerald-700";
    case "pending":
      return "bg-slate-100 text-slate-600";
    case "error":
      return "bg-red-100 text-red-700";
    case "released":
      return "bg-red-50 text-red-600";
    default:
      return "bg-slate-100 text-slate-600";
  }
};

const portingStatusBadge = (status?: string) => {
  switch (status) {
    case "in_progress":
    case "submitted":
      return "bg-amber-100 text-amber-700";
    case "completed":
      return "bg-emerald-100 text-emerald-700";
    case "rejected":
    case "cancelled":
      return "bg-red-100 text-red-700";
    default:
      return "bg-slate-100 text-slate-600";
  }
};

const purposeLabels: Record<string, string> = {
  general: "General",
  intake: "Intake",
  real_estate: "Real Estate",
  immigration: "Immigration",
  billing: "Billing",
  collections: "Collections",
  other: "Other",
};

const countryOptions = [
  { code: "CA", label: "Canada" },
  { code: "US", label: "United States" },
  { code: "GB", label: "United Kingdom" },
  { code: "AU", label: "Australia" },
];

const CANADIAN_CARRIERS = [
  { value: "bell", label: "Bell" },
  { value: "rogers", label: "Rogers" },
  { value: "telus", label: "Telus" },
  { value: "fido", label: "Fido" },
  { value: "koodo", label: "Koodo" },
  { value: "virgin_plus", label: "Virgin Plus" },
  { value: "freedom", label: "Freedom Mobile" },
  { value: "sasktel", label: "SaskTel" },
  { value: "videotron", label: "Videotron" },
  { value: "public_mobile", label: "Public Mobile" },
  { value: "other", label: "Other" },
];

interface PortDocument {
  label: string;
  file: File | null;
  required: boolean;
}

const PORT_STEPS = [
  { num: 1, label: "Number" },
  { num: 2, label: "Details" },
  { num: 3, label: "Review" },
];

const INPUT_CLASS = "w-full rounded-xl border border-slate-200 px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent transition-colors";

const formatFileSize = (bytes: number) => {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1048576) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / 1048576).toFixed(1)} MB`;
};

// ── Component ────────────────────────────────────────────────────────

const PhoneNumbersPage: React.FC = () => {
  const { firmData } = useUserDataContext();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  // Number list state
  const [numbers, setNumbers] = useState<TwilioNumber[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchInput, setSearchInput] = useState("");

  // Modal states
  const [showGetNumber, setShowGetNumber] = useState(false);
  const [showPortNumber, setShowPortNumber] = useState(false);
  const [showDetail, setShowDetail] = useState<TwilioNumber | null>(null);
  const [showReleaseConfirm, setShowReleaseConfirm] = useState<TwilioNumber | null>(null);

  // Get Number flow
  const [searchCountry, setSearchCountry] = useState("CA");
  const [searchCity, setSearchCity] = useState("");
  const [citySuggestions, setCitySuggestions] = useState<Array<{ city: string; region: string; areaCodes: number[] }>>([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const suppressSuggestRef = useRef(false);
  const [matchedCity, setMatchedCity] = useState<string | null>(null);
  const [isNearby, setIsNearby] = useState(false);
  const [searchDone, setSearchDone] = useState(false);
  const [availableNumbers, setAvailableNumbers] = useState<AvailableNumber[]>([]);
  const [availableLoading, setAvailableLoading] = useState(false);
  const [purchasing, setPurchasing] = useState<string | null>(null);
  const [purchaseError, setPurchaseError] = useState("");

  // Port Number wizard
  const [portStep, setPortStep] = useState(1);
  const [portPhone, setPortPhone] = useState("");
  const [portCarrier, setPortCarrier] = useState("");
  const [portCountry, setPortCountry] = useState("CA");
  const [porting, setPorting] = useState(false);
  const [portError, setPortError] = useState("");
  // Step 2: Account details
  const [portAccountNumber, setPortAccountNumber] = useState("");
  const [portAccountPin, setPortAccountPin] = useState("");
  const [portAuthorizedName, setPortAuthorizedName] = useState("");
  const [portServiceAddress, setPortServiceAddress] = useState("");
  // Step 2: Document uploads
  const [portDocuments, setPortDocuments] = useState<PortDocument[]>([
    { label: "Letter of Authorization (LOA)", file: null, required: true },
    { label: "Recent Phone Bill", file: null, required: false },
    { label: "Equipment Record", file: null, required: false },
  ]);
  const portFileInputRefs = useRef<(HTMLInputElement | null)[]>([]);

  // Detail editing
  const [editFriendlyName, setEditFriendlyName] = useState("");
  const [editPurpose, setEditPurpose] = useState("general");
  const [editAssignUser, setEditAssignUser] = useState("");
  const [savingDetail, setSavingDetail] = useState(false);
  const [users, setUsers] = useState<UserItem[]>([]);

  // ── Auto-open port wizard from ?port=true ─────────────────────────
  useEffect(() => {
    if (searchParams.get("port") === "true") {
      setShowPortNumber(true);
      setSearchParams({}, { replace: true });
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const resetPortWizard = () => {
    setPortStep(1);
    setPortPhone("");
    setPortCarrier("");
    setPortCountry("CA");
    setPortError("");
    setPortAccountNumber("");
    setPortAccountPin("");
    setPortAuthorizedName("");
    setPortServiceAddress("");
    setPortDocuments([
      { label: "Letter of Authorization (LOA)", file: null, required: true },
      { label: "Recent Phone Bill", file: null, required: false },
      { label: "Equipment Record", file: null, required: false },
    ]);
  };

  const closePortWizard = () => {
    setShowPortNumber(false);
    resetPortWizard();
  };

  // ── Fetch Numbers ─────────────────────────────────────────────────
  const fetchNumbers = useCallback(async () => {
    setLoading(true);
    try {
      const params: Record<string, any> = { limit: 50 };
      if (searchInput) params.search = searchInput;
      const res = await twilioApi.listNumbers(params);
      const data = res.data as PaginatedResponse<TwilioNumber>;
      setNumbers(data.data);
    } catch (err) {
      console.error("Failed to fetch numbers:", err);
    } finally {
      setLoading(false);
    }
  }, [searchInput]);

  useEffect(() => {
    fetchNumbers();
  }, [fetchNumbers]);

  // Fetch users for assignment
  useEffect(() => {
    usersApi.list({ limit: 100 })
      .then((res) => {
        const data = res.data as PaginatedResponse<UserItem>;
        setUsers(data.data || []);
      })
      .catch(() => {});
  }, []);

  // ── City Autocomplete ────────────────────────────────────────────
  useEffect(() => {
    if (!searchCity || searchCity.length < 2) {
      setCitySuggestions([]);
      return;
    }
    const timer = setTimeout(async () => {
      if (suppressSuggestRef.current) { suppressSuggestRef.current = false; return; }
      try {
        const res = await twilioApi.suggestCities({ q: searchCity, country: searchCountry });
        setCitySuggestions(res.data.data || []);
        setShowSuggestions(true);
      } catch {
        setCitySuggestions([]);
      }
    }, 200);
    return () => clearTimeout(timer);
  }, [searchCity, searchCountry]);

  // ── Auto-search when modal opens based on firm province ─────────
  useEffect(() => {
    if (!showGetNumber) return;
    setPurchaseError("");
    const province = firmData?.jurisdiction?.region?.toUpperCase();
    const city = province ? PROVINCE_MAJOR_CITY[province] : null;
    if (city && !searchDone && availableNumbers.length === 0) {
      setSearchCity(city);
      // Trigger search after state update
      (async () => {
        setAvailableLoading(true);
        setSearchDone(false);
        try {
          const res = await twilioApi.searchAvailable({ country: "CA", city });
          setAvailableNumbers(res.data.data || []);
          setMatchedCity(res.data.matchedCity || null);
          setIsNearby(res.data.isNearby || false);
          setSearchDone(true);
        } catch {
          setSearchDone(true);
        } finally {
          setAvailableLoading(false);
        }
      })();
    }
  }, [showGetNumber]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Search Available Numbers ──────────────────────────────────────
  const handleSearchAvailable = async () => {
    setAvailableLoading(true);
    setSearchDone(false);
    try {
      const res = await twilioApi.searchAvailable({
        country: searchCountry,
        city: searchCity || undefined,
      });
      setAvailableNumbers(res.data.data || []);
      setMatchedCity(res.data.matchedCity || null);
      setIsNearby(res.data.isNearby || false);
      setSearchDone(true);
    } catch (err) {
      console.error("Failed to search:", err);
      setSearchDone(true);
    } finally {
      setAvailableLoading(false);
    }
  };

  // ── Purchase Number ───────────────────────────────────────────────
  const handlePurchase = async (phoneNumber: string) => {
    setPurchasing(phoneNumber);
    setPurchaseError("");
    try {
      await twilioApi.purchase({
        phoneNumber,
        country: searchCountry,
        city: searchCity || null,
        purpose: "general",
      });
      setShowGetNumber(false);
      fetchNumbers();
    } catch (err: any) {
      console.error("Failed to purchase:", err);
      const data = err?.response?.data;
      const status = err?.response?.status;

      if (status === 403 && data?.error === "upgrade_required") {
        setPurchaseError(
          `This feature requires the ${data.requiredPlan || "basic"} plan or higher. Please upgrade your subscription to purchase phone numbers.`
        );
      } else if (status === 402 && data?.error === "twilio_trial") {
        setPurchaseError(data.message || "Your Twilio account is on a trial plan. Upgrade at twilio.com to purchase numbers.");
      } else if (status === 402 && data?.error === "insufficient_funds") {
        setPurchaseError(data.message || "Insufficient funds in your Twilio account.");
      } else if (status === 409 && data?.error === "number_unavailable") {
        setPurchaseError(data.message || "This number is no longer available. Try a different one.");
      } else if (status === 503) {
        setPurchaseError(data?.message || "Phone service is not configured. Check your Twilio credentials in the server environment.");
      } else if (data?.message) {
        setPurchaseError(data.message);
      } else if (data?.details) {
        setPurchaseError(data.details);
      } else if (data?.error && data.error !== "purchase_failed") {
        setPurchaseError(data.error);
      } else {
        setPurchaseError("Failed to purchase this number. Please try again or contact support.");
      }
    } finally {
      setPurchasing(null);
    }
  };

  // ── Port Number ───────────────────────────────────────────────────
  const handlePort = async () => {
    if (!portPhone || !portCarrier) return;
    setPorting(true);
    setPortError("");
    try {
      const formData = new FormData();
      formData.append("phoneNumber", portPhone);
      formData.append("carrier", portCarrier);
      formData.append("country", portCountry);
      if (portAccountNumber) formData.append("accountNumber", portAccountNumber);
      if (portAccountPin) formData.append("accountPin", portAccountPin);
      if (portAuthorizedName) formData.append("authorizedName", portAuthorizedName);
      if (portServiceAddress) formData.append("serviceAddress", portServiceAddress);

      for (const doc of portDocuments) {
        if (doc.file) {
          formData.append("documents", doc.file);
          formData.append("documentLabels", doc.label);
        }
      }

      await twilioApi.portNumber(formData);
      closePortWizard();
      fetchNumbers();
    } catch (err: any) {
      const details = err.response?.data?.details;
      setPortError(
        (Array.isArray(details) && details.length > 0 ? details.join(", ") : null) ||
          err.response?.data?.error ||
          "Failed to submit port request"
      );
    } finally {
      setPorting(false);
    }
  };

  const portCanProceed = (): boolean => {
    switch (portStep) {
      case 1: return !!portPhone.trim() && !!portCarrier;
      case 2: return true; // account details are optional
      case 3: return true;
      default: return false;
    }
  };

  const portGoNext = () => setPortStep((s) => Math.min(s + 1, 3));
  const portGoBack = () => setPortStep((s) => Math.max(s - 1, 1));

  const handlePortDocumentChange = (index: number, file: File | null) => {
    setPortDocuments((prev) =>
      prev.map((d, i) => (i === index ? { ...d, file } : d))
    );
  };

  // ── Release Number ────────────────────────────────────────────────
  const handleRelease = async (number: TwilioNumber) => {
    try {
      await twilioApi.releaseNumber(number._id);
      setShowReleaseConfirm(null);
      setShowDetail(null);
      fetchNumbers();
    } catch (err) {
      console.error("Failed to release:", err);
    }
  };

  // ── Update Number Details ─────────────────────────────────────────
  const openDetail = (num: TwilioNumber) => {
    setShowDetail(num);
    setEditFriendlyName(num.friendlyName || "");
    setEditPurpose(num.usage.purpose);
    setEditAssignUser(num.usage.defaultAssignedUserId || "");
  };

  const handleSaveDetail = async () => {
    if (!showDetail) return;
    setSavingDetail(true);
    try {
      await twilioApi.updateNumber(showDetail._id, {
        friendlyName: editFriendlyName,
        usage: { purpose: editPurpose },
      });
      if (editAssignUser) {
        await twilioApi.assignNumber(showDetail._id, editAssignUser);
      }
      setShowDetail(null);
      fetchNumbers();
    } catch (err) {
      console.error("Failed to update:", err);
    } finally {
      setSavingDetail(false);
    }
  };

  return (
    <div>
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
        <div>
          <h1 className="text-2xl font-bold text-slate-800">Phone Numbers</h1>
          <p className="text-sm text-slate-500 mt-1">
            Manage your firm's Twilio phone numbers
          </p>
        </div>
        <div className="flex gap-2">
          <button
            onClick={() => setShowPortNumber(true)}
            className="inline-flex items-center gap-2 px-4 py-3 text-slate-700 font-medium rounded-xl border border-slate-200 hover:bg-slate-50 transition-colors"
          >
            <ArrowRightLeft className="h-4 w-4" />
            Port a Number
          </button>
          <button
            onClick={() => {
              setShowGetNumber(true);
              setAvailableNumbers([]);
            }}
            className="inline-flex items-center gap-2 px-5 py-3 text-white font-bold rounded-xl transition-colors"
            style={{ backgroundColor: "var(--color-primary)" }}
            onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = "var(--color-primary-hover)")}
            onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = "var(--color-primary)")}
          >
            <Plus className="h-5 w-5" />
            Get a Number
          </button>
        </div>
      </div>

      {/* Search */}
      <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-4 mb-6">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
          <input
            type="text"
            placeholder="Search numbers..."
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            className="w-full pl-10 pr-4 py-3 rounded-xl border border-slate-200 text-sm focus:outline-none focus:border-emerald-600"
          />
        </div>
      </div>

      {/* Numbers List */}
      <div className="bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden">
        {loading ? (
          <div className="flex items-center justify-center py-20">
            <Loader2 className="h-8 w-8 animate-spin text-emerald-600" />
          </div>
        ) : numbers.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-20 text-slate-500">
            <Phone className="h-12 w-12 mb-3 text-slate-300" />
            <p className="font-medium text-lg">No phone numbers</p>
            <p className="text-sm mt-1">Get your first number to start messaging</p>
            <button
              onClick={() => setShowGetNumber(true)}
              className="mt-4 inline-flex items-center gap-2 px-5 py-2.5 text-white font-semibold rounded-xl text-sm"
              style={{ backgroundColor: "var(--color-primary)" }}
            >
              <Plus className="h-4 w-4" />
              Get a Number
            </button>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-slate-200" style={{ backgroundColor: "var(--color-primary)" }}>
                  <th className="text-left px-6 py-3 text-xs font-semibold text-white uppercase tracking-wider">Number</th>
                  <th className="text-left px-6 py-3 text-xs font-semibold text-white uppercase tracking-wider">Status</th>
                  <th className="text-left px-6 py-3 text-xs font-semibold text-white uppercase tracking-wider hidden md:table-cell">Jurisdiction</th>
                  <th className="text-left px-6 py-3 text-xs font-semibold text-white uppercase tracking-wider hidden lg:table-cell">Purpose</th>
                  <th className="text-left px-6 py-3 text-xs font-semibold text-white uppercase tracking-wider hidden xl:table-cell">Assigned</th>
                  <th className="px-6 py-3 text-xs font-semibold text-white uppercase tracking-wider text-right">Actions</th>
                </tr>
              </thead>
              <tbody>
                {numbers.map((num, i) => (
                  <motion.tr
                    key={num._id}
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: i * 0.03 }}
                    className="border-b border-slate-100 hover:bg-slate-50 transition-colors"
                  >
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-3">
                        <div className="h-9 w-9 rounded-xl flex items-center justify-center flex-shrink-0" style={{ backgroundColor: "var(--color-primary-light)" }}>
                          <Phone className="h-4 w-4" style={{ color: "var(--color-primary)" }} />
                        </div>
                        <div>
                          <p className="text-sm font-semibold text-slate-800">
                            {num.friendlyName && num.friendlyName !== num.phoneE164
                              ? num.friendlyName
                              : num.phoneE164}
                          </p>
                          {num.friendlyName && num.friendlyName !== num.phoneE164 && (
                            <p className="text-xs text-slate-400 font-mono">{num.phoneE164}</p>
                          )}
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium ${statusBadge(num.provisioning.status)}`}>
                        {num.provisioning.status}
                      </span>
                      {num.provisioning.method === "ported" && num.provisioning.porting?.status && (
                        <span className={`ml-1 inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${portingStatusBadge(num.provisioning.porting.status)}`}>
                          porting: {num.provisioning.porting.status}
                        </span>
                      )}
                    </td>
                    <td className="px-6 py-4 hidden md:table-cell">
                      <div className="flex items-center gap-1 text-sm text-slate-600">
                        <MapPin className="h-3 w-3 text-slate-400" />
                        {num.jurisdiction.country}
                        {num.jurisdiction.region && ` / ${num.jurisdiction.region}`}
                      </div>
                    </td>
                    <td className="px-6 py-4 hidden lg:table-cell">
                      <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-slate-100 text-slate-600 rounded text-xs">
                        <Tag className="h-3 w-3" />
                        {purposeLabels[num.usage.purpose] || num.usage.purpose}
                      </span>
                    </td>
                    <td className="px-6 py-4 hidden xl:table-cell">
                      <span className="text-sm text-slate-600">
                        {num.usage.defaultAssignedUserId
                          ? users.find((u) => u._id === num.usage.defaultAssignedUserId)?.name || "Assigned"
                          : "-"}
                      </span>
                    </td>
                    <td className="px-6 py-4 text-right">
                      <button
                        onClick={() => openDetail(num)}
                        className="inline-flex items-center gap-1 text-sm text-brand hover:text-brand-hover font-medium"
                      >
                        Manage
                        <ChevronRight className="h-4 w-4" />
                      </button>
                    </td>
                  </motion.tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* ── Get a Number Modal ── */}
      <PremiumModal
        open={showGetNumber}
        onClose={() => setShowGetNumber(false)}
        title="Get a Number"
        subtitle="Search and purchase a new phone number"
        icon={Phone}
      >
        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Country</label>
              <SearchableSelect
                options={countryOptions.map((c) => ({
                  value: c.code,
                  label: c.label,
                  icon: Globe,
                }))}
                value={searchCountry}
                onChange={(val) => {
                  setSearchCountry(val || "CA");
                  setSearchCity("");
                  setCitySuggestions([]);
                  setMatchedCity(null);
                }}
                placeholder="Select country"
                icon={Globe}
              />
            </div>
            <div className="relative">
              <label className="block text-sm font-medium text-slate-700 mb-1">City</label>
              <div className="relative">
                <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                <input
                  type="text"
                  placeholder="e.g. Toronto, Vancouver, New York"
                  value={searchCity}
                  onChange={(e) => {
                    setSearchCity(e.target.value);
                    setMatchedCity(null);
                  }}
                  onFocus={() => citySuggestions.length > 0 && setShowSuggestions(true)}
                  onBlur={() => setTimeout(() => setShowSuggestions(false), 150)}
                  className="w-full pl-9 pr-3 py-3 rounded-xl border border-slate-200 text-sm focus:outline-none focus:border-emerald-600"
                />
              </div>
              {/* City suggestions dropdown */}
              {showSuggestions && citySuggestions.length > 0 && (
                <div className="absolute z-50 w-full mt-1 bg-white rounded-xl border border-slate-200 shadow-lg max-h-48 overflow-y-auto">
                  {citySuggestions.map((s) => (
                    <button
                      key={`${s.city}-${s.region}`}
                      type="button"
                      onMouseDown={(e) => e.preventDefault()}
                      onClick={() => {
                        suppressSuggestRef.current = true;
                        setSearchCity(s.city);
                        setShowSuggestions(false);
                        setCitySuggestions([]);
                      }}
                      className="w-full text-left px-4 py-2.5 hover:bg-slate-50 flex items-center justify-between text-sm border-b border-slate-50 last:border-b-0"
                    >
                      <span className="text-slate-800">{s.city}, {s.region}</span>
                      <span className="text-xs text-slate-400">{s.areaCodes.slice(0, 2).join(", ")}</span>
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>

          <button
            onClick={handleSearchAvailable}
            disabled={availableLoading}
            className="w-full py-3 rounded-xl text-white text-sm font-bold disabled:opacity-50"
            style={{ backgroundColor: "var(--color-primary)" }}
          >
            {availableLoading ? "Searching..." : "Search Available Numbers"}
          </button>

          {searchDone && !availableLoading && matchedCity && availableNumbers.length > 0 && !isNearby && (
            <p className="text-xs text-slate-500 text-center">
              Showing numbers for <span className="font-medium text-slate-700">{matchedCity}</span>
            </p>
          )}

          {searchDone && !availableLoading && isNearby && availableNumbers.length > 0 && (
            <div className="p-3 rounded-xl bg-blue-50 border border-blue-200">
              <p className="text-xs text-blue-700">
                No numbers available directly in <span className="font-semibold">{matchedCity || searchCity}</span>. Showing nearby numbers from the same region. Check back in a few hours  -  local numbers often become available for short periods.
              </p>
            </div>
          )}

          {searchDone && !availableLoading && availableNumbers.length === 0 && searchCity && (
            <div className="p-4 rounded-xl bg-slate-50 border border-slate-200 text-center space-y-2">
              <p className="text-sm text-slate-600">
                No local numbers available near <span className="font-semibold">{matchedCity || searchCity}</span>.
              </p>
              <p className="text-xs text-slate-500">
                Try a different city, or search without a city to see all available numbers for this country.
              </p>
            </div>
          )}

          {purchaseError && (
            <div role="alert" className="p-3 rounded-xl bg-red-50 border border-red-200 text-sm text-red-700 flex items-start gap-2">
              <AlertTriangle className="h-4 w-4 flex-shrink-0 mt-0.5" />
              {purchaseError}
            </div>
          )}

          {availableNumbers.length > 0 && (
            <div className="space-y-2 max-h-72 overflow-y-auto pr-1">
              {availableNumbers.map((n) => {
                const location = [n.city, n.region].filter(Boolean).join(", ") || n.country;
                const isPurchasing = purchasing === n.phoneNumber;
                return (
                  <div
                    key={n.phoneNumber}
                    className="flex items-center gap-3 p-3.5 rounded-xl border border-slate-200 hover:border-slate-300 hover:shadow-sm transition-all bg-white"
                  >
                    <div className="h-10 w-10 rounded-xl flex items-center justify-center flex-shrink-0" style={{ backgroundColor: "var(--color-primary-light)" }}>
                      <Phone className="h-4.5 w-4.5" style={{ color: "var(--color-primary)" }} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-bold text-slate-800 tracking-wide">
                        {n.friendlyName || n.phoneNumber}
                      </p>
                      <div className="flex items-center gap-2 mt-0.5">
                        <span className="inline-flex items-center gap-1 text-xs text-slate-400">
                          <MapPin className="h-3 w-3" />
                          {location}
                        </span>
                        {(n.capabilities.sms || n.capabilities.voice) && (
                          <span className="px-1.5 py-0.5 rounded bg-blue-50 text-blue-600 text-[10px] font-semibold">SMS</span>
                        )}
                        {n.capabilities.voice && (
                          <span className="px-1.5 py-0.5 rounded bg-purple-50 text-purple-600 text-[10px] font-semibold">Voice</span>
                        )}
                        {(n.capabilities.mms || n.capabilities.sms || n.capabilities.voice) && (
                          <span className="px-1.5 py-0.5 rounded bg-cyan-50 text-cyan-600 text-[10px] font-semibold">MMS</span>
                        )}
                      </div>
                    </div>
                    <button
                      onClick={() => handlePurchase(n.phoneNumber)}
                      disabled={purchasing !== null}
                      className="inline-flex items-center gap-1.5 px-4 py-2 rounded-xl text-white text-xs font-bold disabled:opacity-50 flex-shrink-0 transition-colors"
                      style={{ backgroundColor: "var(--color-primary)" }}
                      onMouseEnter={(e) => !isPurchasing && (e.currentTarget.style.backgroundColor = "var(--color-primary-hover)")}
                      onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = "var(--color-primary)")}
                    >
                      {isPurchasing ? (
                        <Loader2 className="h-4 w-4 animate-spin" />
                      ) : (
                        <>
                          <Plus className="h-3.5 w-3.5" />
                          Get
                        </>
                      )}
                    </button>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </PremiumModal>

      {/* ── Port a Number — 3-Step Wizard ── */}
      <PremiumModal
        open={showPortNumber}
        onClose={closePortWizard}
        title="Port a Number"
        subtitle={`Step ${portStep} of 3 — ${PORT_STEPS[portStep - 1].label}`}
        icon={ArrowRightLeft}
        size="md"
        footer={
          <div className="flex items-center justify-between">
            <div>
              {portStep > 1 && (
                <button
                  onClick={portGoBack}
                  className="inline-flex items-center gap-1.5 px-4 py-2.5 rounded-xl border border-slate-200 text-sm font-medium text-slate-600 hover:bg-slate-50"
                >
                  <ChevronLeft className="h-4 w-4" />
                  Back
                </button>
              )}
            </div>
            <div className="flex gap-3">
              <button
                onClick={closePortWizard}
                className="px-5 py-2.5 rounded-xl border border-slate-200 text-sm font-medium text-slate-600 hover:bg-slate-50"
              >
                Cancel
              </button>
              {portStep < 3 ? (
                <button
                  onClick={portGoNext}
                  disabled={!portCanProceed()}
                  className="inline-flex items-center gap-1.5 px-5 py-2.5 rounded-xl text-white text-sm font-bold disabled:opacity-50"
                  style={{ backgroundColor: "var(--color-primary)" }}
                >
                  Next
                  <ChevronRight className="h-4 w-4" />
                </button>
              ) : (
                <button
                  onClick={handlePort}
                  disabled={porting || !portPhone || !portCarrier}
                  className="px-5 py-2.5 rounded-xl text-white text-sm font-bold disabled:opacity-50"
                  style={{ backgroundColor: "var(--color-primary)" }}
                >
                  {porting ? "Submitting..." : "Submit Port Request"}
                </button>
              )}
            </div>
          </div>
        }
      >
        {/* Progress Bar */}
        <div className="flex items-center justify-between px-2 mb-6">
          {PORT_STEPS.map((s, i) => (
            <React.Fragment key={s.num}>
              <div className="flex flex-col items-center gap-1.5">
                <div
                  className={`h-8 w-8 rounded-full flex items-center justify-center text-xs font-bold transition-colors ${
                    portStep > s.num
                      ? "bg-emerald-500 text-white"
                      : portStep === s.num
                        ? "text-white"
                        : "bg-slate-100 text-slate-400"
                  }`}
                  style={portStep === s.num ? { backgroundColor: "var(--color-primary)" } : undefined}
                >
                  {portStep > s.num ? <CheckCircle2 className="h-4 w-4" /> : s.num}
                </div>
                <span className={`text-xs font-medium ${portStep >= s.num ? "text-slate-700" : "text-slate-400"}`}>
                  {s.label}
                </span>
              </div>
              {i < PORT_STEPS.length - 1 && (
                <div className={`flex-1 h-0.5 mx-3 mt-[-18px] rounded ${portStep > s.num ? "bg-emerald-500" : "bg-slate-200"}`} />
              )}
            </React.Fragment>
          ))}
        </div>

        {portError && (
          <div className="mb-4 p-3 rounded-xl bg-red-50 border border-red-200 text-sm text-red-700 flex items-start gap-2">
            <AlertTriangle className="h-4 w-4 flex-shrink-0 mt-0.5" />
            {portError}
          </div>
        )}

        {/* Step 1: Number & Carrier */}
        {portStep === 1 && (
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Phone Number *</label>
              <input
                type="tel"
                value={portPhone}
                onChange={(e) => setPortPhone(e.target.value)}
                className={INPUT_CLASS}
                placeholder="+14165551234"
              />
              <p className="text-xs text-slate-400 mt-1">Enter in E.164 format (e.g. +14165551234)</p>
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Current Carrier *</label>
              <SearchableSelect
                options={CANADIAN_CARRIERS.map((c) => ({
                  value: c.value,
                  label: c.label,
                  icon: Phone,
                }))}
                value={portCarrier}
                onChange={(val) => setPortCarrier(val || "")}
                placeholder="Select your current carrier"
                icon={Phone}
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Country</label>
              <SearchableSelect
                options={countryOptions.map((c) => ({
                  value: c.code,
                  label: c.label,
                  icon: Globe,
                }))}
                value={portCountry}
                onChange={(val) => setPortCountry(val || "CA")}
                placeholder="Select country"
                icon={Globe}
              />
            </div>
            <div className="p-3 rounded-xl bg-blue-50 border border-blue-200">
              <p className="text-xs text-blue-700 flex items-start gap-2">
                <Info className="h-3.5 w-3.5 flex-shrink-0 mt-0.5" />
                Not sure what you need?{" "}
                <button
                  type="button"
                  onClick={() => { closePortWizard(); navigate("/settings/port-guide"); }}
                  className="font-semibold underline hover:no-underline"
                >
                  Read our Porting Guide
                </button>
              </p>
            </div>
          </div>
        )}

        {/* Step 2: Account Details & Documents */}
        {portStep === 2 && (
          <div className="space-y-5">
            {/* Account Info */}
            <div>
              <h3 className="text-sm font-semibold text-slate-800 mb-3">Account Information</h3>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-medium text-slate-600 mb-1">Account Number</label>
                  <input
                    type="text"
                    value={portAccountNumber}
                    onChange={(e) => setPortAccountNumber(e.target.value)}
                    className={INPUT_CLASS}
                    placeholder="e.g. 123456789"
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-slate-600 mb-1">Account PIN / Password</label>
                  <input
                    type="password"
                    value={portAccountPin}
                    onChange={(e) => setPortAccountPin(e.target.value)}
                    className={INPUT_CLASS}
                    placeholder="Enter PIN"
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-slate-600 mb-1">Authorized Name on Account</label>
                  <input
                    type="text"
                    value={portAuthorizedName}
                    onChange={(e) => setPortAuthorizedName(e.target.value)}
                    className={INPUT_CLASS}
                    placeholder="Must match exactly"
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-slate-600 mb-1">Service Address on File</label>
                  <input
                    type="text"
                    value={portServiceAddress}
                    onChange={(e) => setPortServiceAddress(e.target.value)}
                    className={INPUT_CLASS}
                    placeholder="Address on carrier account"
                  />
                </div>
              </div>
            </div>

            {/* Documents */}
            <div>
              <h3 className="text-sm font-semibold text-slate-800 mb-3">Supporting Documents</h3>
              <div className="space-y-3">
                {portDocuments.map((doc, i) => (
                  <div
                    key={doc.label}
                    className="flex items-center gap-3 p-3 rounded-xl border border-slate-200 bg-white"
                  >
                    <div className="h-9 w-9 rounded-lg bg-slate-100 flex items-center justify-center flex-shrink-0">
                      <FileText className="h-4 w-4 text-slate-500" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <p className="text-sm font-medium text-slate-700">{doc.label}</p>
                        <span className={`px-1.5 py-0.5 rounded text-[10px] font-semibold ${
                          doc.required ? "bg-red-50 text-red-600" : "bg-slate-100 text-slate-500"
                        }`}>
                          {doc.required ? "Required" : "Recommended"}
                        </span>
                      </div>
                      {doc.file ? (
                        <p className="text-xs text-slate-500 mt-0.5 truncate">
                          {doc.file.name} ({formatFileSize(doc.file.size)})
                        </p>
                      ) : (
                        <p className="text-xs text-slate-400 mt-0.5">PDF, JPG, PNG, DOC — up to 25 MB</p>
                      )}
                    </div>
                    <input
                      ref={(el) => { portFileInputRefs.current[i] = el; }}
                      type="file"
                      accept=".pdf,.jpg,.jpeg,.png,.doc,.docx"
                      className="hidden"
                      onChange={(e) => handlePortDocumentChange(i, e.target.files?.[0] || null)}
                    />
                    {doc.file ? (
                      <div className="flex items-center gap-2 flex-shrink-0">
                        <CheckCircle2 className="h-4 w-4 text-emerald-500" />
                        <button
                          onClick={() => handlePortDocumentChange(i, null)}
                          className="text-slate-400 hover:text-red-500 transition-colors"
                        >
                          <X className="h-4 w-4" />
                        </button>
                      </div>
                    ) : (
                      <button
                        onClick={() => portFileInputRefs.current[i]?.click()}
                        className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-slate-200 text-xs font-medium text-slate-600 hover:bg-slate-50 flex-shrink-0"
                      >
                        <Upload className="h-3 w-3" />
                        Upload
                      </button>
                    )}
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* Step 3: Review */}
        {portStep === 3 && (
          <div className="space-y-4">
            {/* Number info */}
            <div className="p-4 rounded-xl bg-slate-50 border border-slate-200">
              <h4 className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-2">Number & Carrier</h4>
              <div className="grid grid-cols-3 gap-3 text-sm">
                <div>
                  <p className="text-slate-500 text-xs">Phone Number</p>
                  <p className="font-semibold text-slate-800">{portPhone || "—"}</p>
                </div>
                <div>
                  <p className="text-slate-500 text-xs">Carrier</p>
                  <p className="font-semibold text-slate-800">
                    {CANADIAN_CARRIERS.find((c) => c.value === portCarrier)?.label || portCarrier || "—"}
                  </p>
                </div>
                <div>
                  <p className="text-slate-500 text-xs">Country</p>
                  <p className="font-semibold text-slate-800">
                    {countryOptions.find((c) => c.code === portCountry)?.label || portCountry}
                  </p>
                </div>
              </div>
            </div>

            {/* Account details */}
            <div className="p-4 rounded-xl bg-slate-50 border border-slate-200">
              <h4 className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-2">Account Details</h4>
              <div className="grid grid-cols-2 gap-3 text-sm">
                <div>
                  <p className="text-slate-500 text-xs">Account Number</p>
                  <p className="font-medium text-slate-700">{portAccountNumber || "—"}</p>
                </div>
                <div>
                  <p className="text-slate-500 text-xs">Account PIN</p>
                  <p className="font-medium text-slate-700 flex items-center gap-1">
                    {portAccountPin ? (
                      <><Lock className="h-3 w-3 text-slate-400" /> {"•".repeat(portAccountPin.length)}</>
                    ) : "—"}
                  </p>
                </div>
                <div>
                  <p className="text-slate-500 text-xs">Authorized Name</p>
                  <p className="font-medium text-slate-700">{portAuthorizedName || "—"}</p>
                </div>
                <div>
                  <p className="text-slate-500 text-xs">Service Address</p>
                  <p className="font-medium text-slate-700">{portServiceAddress || "—"}</p>
                </div>
              </div>
            </div>

            {/* Documents */}
            <div className="p-4 rounded-xl bg-slate-50 border border-slate-200">
              <h4 className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-2">Attached Documents</h4>
              {portDocuments.some((d) => d.file) ? (
                <div className="space-y-2">
                  {portDocuments.filter((d) => d.file).map((d) => (
                    <div key={d.label} className="flex items-center gap-2 text-sm">
                      <CheckCircle2 className="h-4 w-4 text-emerald-500 flex-shrink-0" />
                      <span className="text-slate-700 font-medium">{d.label}</span>
                      <span className="text-xs text-slate-400">— {d.file!.name} ({formatFileSize(d.file!.size)})</span>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-sm text-slate-500">No documents attached</p>
              )}
            </div>

            {/* LOA warning */}
            {!portDocuments[0].file && (
              <div className="p-3 rounded-xl bg-amber-50 border border-amber-200 flex items-start gap-2">
                <AlertTriangle className="h-4 w-4 text-amber-600 flex-shrink-0 mt-0.5" />
                <p className="text-xs text-amber-700">
                  We strongly recommend attaching a <strong>Letter of Authorization (LOA)</strong>. Without it, the port request may be delayed or rejected by your carrier.
                </p>
              </div>
            )}
          </div>
        )}
      </PremiumModal>

      {/* ── Number Detail / Edit Modal ── */}
      <PremiumModal
        open={!!showDetail}
        onClose={() => setShowDetail(null)}
        title={showDetail?.phoneE164 || "Phone Number"}
        subtitle={showDetail ? showDetail.provisioning.status : ""}
        icon={Edit3}
        size="sm"
        footer={
          showDetail ? (
            <div className="flex justify-between">
              <button
                onClick={() => setShowReleaseConfirm(showDetail)}
                className="inline-flex items-center gap-1.5 px-4 py-2.5 rounded-xl border border-red-200 text-sm font-medium text-red-600 hover:bg-red-50"
              >
                <Trash2 className="h-4 w-4" />
                Release
              </button>
              <div className="flex gap-2">
                <button
                  onClick={() => setShowDetail(null)}
                  className="px-5 py-2.5 rounded-xl border border-slate-200 text-sm font-medium text-slate-600 hover:bg-slate-50"
                >
                  Cancel
                </button>
                <button
                  onClick={handleSaveDetail}
                  disabled={savingDetail}
                  className="px-5 py-2.5 rounded-xl text-white text-sm font-bold disabled:opacity-50"
                  style={{ backgroundColor: "var(--color-primary)" }}
                >
                  {savingDetail ? "Saving..." : "Save Changes"}
                </button>
              </div>
            </div>
          ) : undefined
        }
      >
        {showDetail && (
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">
                <Edit3 className="inline h-3.5 w-3.5 mr-1" />
                Friendly Name
              </label>
              <input
                type="text"
                value={editFriendlyName}
                onChange={(e) => setEditFriendlyName(e.target.value)}
                className="w-full rounded-xl border border-slate-200 px-4 py-3 text-sm focus:outline-none focus:border-emerald-600"
                placeholder="e.g. Main Office Line"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">
                <Tag className="inline h-3.5 w-3.5 mr-1" />
                Purpose
              </label>
              <SearchableSelect
                options={Object.entries(purposeLabels).map(([key, label]) => ({
                  value: key,
                  label,
                  icon: Tag,
                }))}
                value={editPurpose}
                onChange={(val) => setEditPurpose(val || "general")}
                placeholder="Select purpose"
                icon={Tag}
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">
                <User className="inline h-3.5 w-3.5 mr-1" />
                Assign to User
              </label>
              <SearchableSelect
                options={[
                  { value: "", label: "Unassigned" },
                  ...users.map((u) => ({
                    value: u._id,
                    label: u.name || u.email || u._id,
                    icon: User,
                  })),
                ]}
                value={editAssignUser}
                onChange={(val) => setEditAssignUser(val || "")}
                placeholder="Select user"
                icon={User}
                clearable
              />
            </div>

            {/* Compliance info */}
            <div className="p-3 rounded-xl bg-slate-50 border border-slate-100">
              <p className="text-xs font-semibold text-slate-600 mb-2 flex items-center gap-1">
                <Shield className="h-3.5 w-3.5" />
                Compliance
              </p>
              <div className="flex gap-3 text-xs text-slate-500">
                <span>Opt-in: {showDetail.compliance.requireOptIn ? "Required" : "Not required"}</span>
                <span>STOP: {showDetail.compliance.stopKeywordsEnabled ? "Enabled" : "Disabled"}</span>
                <span>Consent: {showDetail.compliance.consentPolicy}</span>
              </div>
            </div>
          </div>
        )}
      </PremiumModal>

      {/* ── Release Confirmation ── */}
      <PremiumModal
        open={!!showReleaseConfirm}
        onClose={() => setShowReleaseConfirm(null)}
        title="Release Number?"
        icon={AlertTriangle}
        size="sm"
        footer={
          <div className="flex justify-end gap-3">
            <button
              onClick={() => setShowReleaseConfirm(null)}
              className="px-5 py-2.5 rounded-xl border border-slate-200 text-sm font-medium text-slate-600 hover:bg-slate-50"
            >
              Cancel
            </button>
            <button
              onClick={() => showReleaseConfirm && handleRelease(showReleaseConfirm)}
              className="px-5 py-2.5 rounded-xl bg-red-600 text-white text-sm font-bold hover:bg-red-700"
            >
              Release Number
            </button>
          </div>
        }
      >
        {showReleaseConfirm && (
          <p className="text-sm text-slate-600">
            Are you sure you want to release <strong>{showReleaseConfirm.phoneE164}</strong>? This action cannot be undone and the number will be returned to Twilio.
          </p>
        )}
      </PremiumModal>
    </div>
  );
};

export default PhoneNumbersPage;
